import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteSeoDocument, SITE_NAME } from "../seo";

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function upsertScript(selector: string, id: string, content: string) {
  let element = document.head.querySelector(selector) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = content;
}

export function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    const seo = getRouteSeoDocument(location.pathname);

    document.title = seo.title;
    document.documentElement.lang = seo.htmlLang;

    upsertMeta('meta[name="description"]', { name: "description", content: seo.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: seo.robots });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: seo.ogType });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: seo.ogLocale });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: seo.description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: seo.canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: seo.ogImageUrl });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: seo.ogImageAlt });
    upsertMeta('meta[property="og:image:type"]', { property: "og:image:type", content: "image/png" });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seo.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seo.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: seo.ogImageUrl });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: seo.ogImageAlt });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: seo.canonicalUrl });
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    seo.alternateLinks.forEach((link) => {
      upsertLink(`link[rel="alternate"][hreflang="${link.hreflang}"]`, {
        rel: "alternate",
        hreflang: link.hreflang,
        href: link.href,
      });
    });
    upsertScript('script#route-structured-data', "route-structured-data", seo.structuredData);
  }, [location.pathname]);

  return null;
}
